/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { fetchIncidents, fetchFilteredIncidents, exportIncidents, emailExportIncidents, uploadIncidentImage, updateIncidentImage, deleteIncidentImage } from './incidentsApi';
import { IncidentsState, IncidentsResponseData, DEFAULT_PAGE_SIZE } from './types';

const initialState = {
    error: null,
    isFetching: false,
    incidents: [],
    pagination: {
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
    },
    filterData: {
        statuses: [],
        priorities: [],
        problemCategories: [],
        remedyReferences: [],
        sites: [],
    },
    summaryData: {
        new: 0,
        active: 0,
        critical: 0,
        completed: 0,
    },
    isExporting: false,
    incidentDetail: null,
    incidentDetailError: null,
} as IncidentsState;

const handleFulfilled = (
    state: IncidentsState,
    action: PayloadAction<IncidentsResponseData>,
) => {
    state.isFetching = false;
    if (action.payload.incidents) {
        state.incidents = action.payload.incidents.data;
        state.pagination = {
            currentPage: action.payload.incidents.meta.current_page || 1,
            pageSize: action.payload.incidents.meta.per_page || DEFAULT_PAGE_SIZE,
            total: action.payload.incidents.meta.total || 0,
        };
    } else {
        state.incidents = [];
        state.pagination = {
            currentPage: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            total: 0,
        };
    }
    if (action.payload.filters) {
        state.filterData = action.payload.filters;
    }
    if (action.payload.summary) {
        state.summaryData = action.payload.summary;
    }
};

export const incidentsSlice = createSlice({
    name: 'incidents',
    initialState,
    reducers: {
        resetIncidentsPagination: (state: IncidentsState) => {
            state.pagination = {
                currentPage: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                total: 0,
            };
        },
        clearIncidentDetail: (state: IncidentsState) => {
            state.incidentDetail = null;
            state.incidentDetailError = null;
        },
        clearIncidents: (state: IncidentsState) => {
            state.incidents = [];
            state.isFetching = true;
            state.pagination = {
                currentPage: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                total: 0,
            };
            state.summaryData = {
                new: 0,
                active: 0,
                critical: 0,
                completed: 0,
            };
            state.filterData = {
                statuses: [],
                priorities: [],
                problemCategories: [],
                remedyReferences: [],
                sites: [],
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIncidents.fulfilled, handleFulfilled);

        builder
            .addCase(fetchFilteredIncidents.fulfilled, handleFulfilled);

        // Export state
        builder.addCase(exportIncidents.pending, (state: IncidentsState) => {
            state.isExporting = true;
        });
        builder.addCase(exportIncidents.fulfilled, (state: IncidentsState) => {
            state.isExporting = false;
        });
        builder.addCase(exportIncidents.rejected, (state: IncidentsState) => {
            state.isExporting = false;
        });

        // Email export state
        builder.addCase(emailExportIncidents.pending, (state: IncidentsState) => {
            state.isExporting = true;
        });
        builder.addCase(emailExportIncidents.fulfilled, (state: IncidentsState) => {
            state.isExporting = false;
        });
        builder.addCase(emailExportIncidents.rejected, (state: IncidentsState) => {
            state.isExporting = false;
        });

        // Incident image operations
        builder.addCase(uploadIncidentImage.fulfilled, (state, action) => {
            const incident = state.incidents.find((i) => i.id === action.payload.incidentId);
            if (incident) {
                incident.images.push(action.payload.incidentImage);
            }
        });
        builder.addCase(updateIncidentImage.fulfilled, (state, action) => {
            const incident = state.incidents.find((i) => i.id === action.payload.incidentId);
            if (incident) {
                const image = incident.images.find((img) => img.id === action.payload.incidentImage.id);
                if (image) {
                    image.description = action.payload.incidentImage.description;
                }
            }
        });
        builder.addCase(deleteIncidentImage.fulfilled, (state, action) => {
            const incident = state.incidents.find((i) => i.id === action.payload.incidentId);
            if (incident) {
                incident.images = incident.images.filter((img) => img.id !== action.payload.imageId);
            }
        });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                fetchIncidents.pending,
                fetchFilteredIncidents.pending,
            ),
            (state: IncidentsState) => {
                state.isFetching = true;
                state.error = null;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                fetchIncidents.rejected,
                fetchFilteredIncidents.rejected,
            ),
            (state: IncidentsState, action) => {
                state.isFetching = false;
                state.error = action.payload ?? action.error;
                state.incidents = [];
                state.pagination = {
                    currentPage: 1,
                    pageSize: DEFAULT_PAGE_SIZE,
                    total: 0,
                };
                state.summaryData = { new: 0, active: 0, critical: 0, completed: 0 };
                state.filterData = {
                    statuses: [],
                    priorities: [],
                    problemCategories: [],
                    remedyReferences: [],
                    sites: [],
                };
            },
        );
    },
});

// this is for configureStore
export default incidentsSlice.reducer;
export const { resetIncidentsPagination, clearIncidentDetail, clearIncidents } = incidentsSlice.actions;
